import { useCollectionView } from '../../app/use-collection-view.js';
import {
  Button,
  Combobox,
  CurrencyInput,
  DatePicker,
  DateRangePicker,
  IconButton,
  Input,
  Modal,
  MultiSelect,
  Select,
  StatusPill,
  Textarea,
  formatCivilRange,
  formatCurrency,
  useIsWideViewport,
  type CivilDate,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Barcode, CheckCheck, Paperclip, PencilLine, Receipt, Trash2, Undo2 } from 'lucide-react';
import { useMemo, useState, type MouseEvent } from 'react';
import {
  contributionsApi,
  entriesApi,
  getApiError,
  partnersApi,
  type Entry,
  type EntryInput,
  type EntryPeriodBy,
  type EntrySort,
  type EntryStatus,
  type PaidBy,
  type PaymentMethod,
  type PaymentTerm,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { EntryDetailsDialog } from './entry-details.js';
import {
  declarableStatuses,
  isSettleable,
  methodLabels,
  payerLabels,
  payerNameOf,
  settledLabelOf,
  statusLabels,
  statusTones,
  termLabels,
} from './entry-labels.js';
import { EntryReceiptsDialog } from './entry-receipts.js';
import { EntrySlipsDialog } from './entry-slips.js';
import { ReceiptPicker } from './receipt-picker.js';
import { describePending, type PendingReceipt } from '../../app/receipt-staging.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
  SummaryStrip,
} from '../../app/data-view.js';
import type { EntriesView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

export interface EntryCapabilities {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canSettle: boolean;
  canManageReceipts: boolean;
  canManageSlips: boolean;
}

/// As opções dos filtros de escolha, todas as que o lançamento pode ter: com várias marcáveis, a
/// pergunta "o que falta reembolsar ou pagar" é um recorte só, e não duas consultas somadas à mão.
const statusOptions = (Object.keys(statusLabels) as EntryStatus[]).map((value) => ({
  value,
  label: statusLabels[value],
}));
const methodOptions = (Object.keys(methodLabels) as PaymentMethod[]).map((value) => ({
  value,
  label: methodLabels[value],
}));

/// "Pago por": os tipos de pagador sem cadastro primeiro, os sócios pelo nome depois. Qualquer sócio
/// é marcar todos eles; o terceiro se acha pelo nome na busca, que já procura quem pagou.
const payerKindOptions = [
  { value: 'COMPANY', label: 'Empresa' },
  { value: 'THIRD_PARTY', label: 'Terceiros' },
];

const periodLabels: Record<EntryPeriodBy, string> = {
  purchasedOn: 'Compra',
  dueOn: 'Vence',
  settledOn: 'Baixa',
};

const columns: Array<{ sort: EntrySort; label: string }> = [
  { sort: 'purchasedOn', label: 'Compra' },
  { sort: 'description', label: 'Descrição' },
  { sort: 'amount', label: 'Valor' },
  { sort: 'dueOn', label: 'Vencimento' },
];

interface FormState {
  purchasedOn: CivilDate | null;
  description: string;
  amountCents: number | null;
  paymentMethod: PaymentMethod;
  paymentTerm: PaymentTerm;
  paidBy: PaidBy;
  paidByPartnerId: string;
  thirdPartyName: string;
  dueOn: CivilDate | null;
  installmentCount: string;
  declaredStatus: EntryStatus;
  payeeName: string;
  /// Como a compra da empresa foi paga. "Por fora" grava a baixa manual de todas as parcelas no
  /// cadastro; "pela conta" deixa as parcelas abertas esperando a conciliação (ADR 0025).
  paidOutside: boolean;
  paidOutsideOn: CivilDate | null;
  reimburseTo: string;
  reimburseAt: string;
  contributionId: string;
  notes: string;
}

function emptyForm(today: CivilDate): FormState {
  return {
    purchasedOn: today,
    description: '',
    amountCents: null,
    paymentMethod: 'PIX',
    paymentTerm: 'IMMEDIATE',
    paidBy: 'PARTNER',
    paidByPartnerId: '',
    thirdPartyName: '',
    dueOn: today,
    installmentCount: '',
    declaredStatus: 'TO_REIMBURSE',
    payeeName: '',
    paidOutside: false,
    paidOutsideOn: today,
    reimburseTo: '',
    reimburseAt: '',
    contributionId: '',
    notes: '',
  };
}

function fromEntry(entry: Entry): FormState {
  return {
    purchasedOn: entry.purchasedOn,
    description: entry.description,
    amountCents: entry.amountCents,
    paymentMethod: entry.paymentMethod,
    paymentTerm: entry.paymentTerm,
    paidBy: entry.paidBy,
    paidByPartnerId: entry.paidByPartner?.id ?? '',
    thirdPartyName: entry.thirdPartyName ?? '',
    dueOn: entry.dueOn,
    installmentCount: entry.installmentCount === null ? '' : String(entry.installmentCount),
    declaredStatus: entry.declaredStatus,
    payeeName: entry.payeeName ?? '',
    // Editar não repaga nada: a baixa de uma compra que já existe vive na lista de parcelas.
    paidOutside: false,
    paidOutsideOn: entry.purchasedOn,
    reimburseTo: entry.reimburseTo ?? '',
    reimburseAt: entry.reimburseAt ?? '',
    contributionId: entry.contribution?.id ?? '',
    notes: entry.notes ?? '',
  };
}

export function CompanyEntries({
  capabilities,
  consolidated = false,
  timeZone,
  today,
  view,
  onView,
}: {
  capabilities: EntryCapabilities;
  /// Sem unidade escolhida numa empresa de várias: os lançamentos das unidades alcançadas, só leitura.
  consolidated?: boolean;
  timeZone: string;
  today: CivilDate;
  view: EntriesView;
  onView: ViewSetter<EntriesView>;
}) {
  const queryClient = useQueryClient();
  const {
    search,
    statuses,
    methods,
    payers,
    from,
    to,
    periodBy,
    sort,
    direction,
    limit,
    offset,
    entry: openEntryId,
    entryTab,
  } = view;

  /// Fechar a ficha tira as duas coisas do endereço de uma vez: sem isso, sair de comprovantes
  /// deixaria `entryTab` pendurado num endereço sem ficha.
  const closeEntry = () => onView({ entry: '', entryTab: '' });
  const openEntry = (id: string) => onView({ entry: id, entryTab: '' });
  const openEntryTab = (id: string, tab: 'receipts' | 'slips') =>
    onView({ entry: id, entryTab: tab });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Entry>();
  const [form, setForm] = useState<FormState>(() => emptyForm(today));
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  /// Comprovantes escolhidos antes de o lançamento existir. Só sobem depois que há a quem
  /// pertencer — anexo é de um lançamento, não do formulário.
  const [pendingReceipts, setPendingReceipts] = useState<PendingReceipt[]>([]);

  /// Baixa de parcela, edição e exclusão são passos de uma ação, e continuam sem endereço: link de
  /// confirmação de exclusão é uma armadilha.
  const [installmentsTarget, setInstallmentsTarget] = useState<Entry>();
  const [deleteTarget, setDeleteTarget] = useState<Entry>();

  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';

  const filters = {
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(statuses ? { statuses } : {}),
    ...(methods ? { methods } : {}),
    ...(payers ? { payers } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    // A referência só viaja junto do recorte: sem dias escolhidos, ela não recorta nada.
    ...((from ?? to) ? { periodBy } : {}),
    sort,
    direction,
    limit,
    offset,
  };
  const page = useQuery({
    queryKey: ['company', 'entries', filters],
    queryFn: () => entriesApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const partners = useQuery({
    queryKey: ['company', 'partners', 'active'],
    queryFn: () => partnersApi.list({ activity: 'ACTIVE', sort: 'displayName', direction: 'asc' }),
    retry: false,
  });
  const contributions = useQuery({
    queryKey: ['company', 'contributions', 'options'],
    queryFn: () =>
      contributionsApi.list({
        type: 'ALL',
        status: 'ALL',
        sort: 'contributedOn',
        direction: 'desc',
      }),
    retry: false,
  });

  const partnerOptions = useMemo(
    () => [
      { value: '', label: 'Escolha o sócio' },
      ...(partners.data ?? []).map((partner) => ({
        value: partner.id,
        label: partner.displayName,
      })),
    ],
    [partners.data],
  );
  const payerOptions = useMemo(
    () => [
      ...payerKindOptions,
      ...(partners.data ?? []).map((partner) => ({
        value: `partner:${partner.id}`,
        label: partner.displayName,
      })),
    ],
    [partners.data],
  );

  /// Etiqueta de um filtro de várias escolhas: o nome quando é uma só, a conta quando são mais.
  function choiceChip(
    key: string,
    options: ReadonlyArray<{ value: string; label: string }>,
    selected: readonly string[] | undefined,
    noun: string,
    clear: Partial<EntriesView>,
  ) {
    if (selected === undefined) return [];
    const label =
      selected.length === 1
        ? (options.find((option) => option.value === selected[0])?.label ?? `1 ${noun}`)
        : `${String(selected.length)} ${noun}`;
    return [{ key, label, onRemove: () => onView(clear) }];
  }

  /// O que está filtrando agora, para virar etiqueta na barra do celular. A busca fica de fora:
  /// ela já está visível no próprio campo, e repeti-la em etiqueta só ocuparia espaço.
  const activeFilters = [
    ...choiceChip('status', statusOptions, statuses, 'situações', { statuses: undefined }),
    ...choiceChip('method', methodOptions, methods, 'formas', { methods: undefined }),
    ...choiceChip('payer', payerOptions, payers, 'pagadores', { payers: undefined }),
    ...((from ?? to)
      ? [
          {
            key: 'range',
            label: `${periodLabels[periodBy]} ${formatCivilRange({ from, to }, '')}`,
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  function clearFilters() {
    onView({
      statuses: undefined,
      methods: undefined,
      payers: undefined,
      from: null,
      to: null,
    });
  }

  const contributionOptions = useMemo(
    () => [
      { value: '', label: 'Sem vínculo' },
      ...(contributions.data?.contributions ?? []).map((contribution) => ({
        value: contribution.id,
        label: contribution.description,
      })),
    ],
    [contributions.data],
  );
  const chosenPartner = (partners.data ?? []).find(
    (partner) => partner.id === form.paidByPartnerId,
  );
  /// As contas cadastradas do sócio viram sugestão de "Reembolsar onde", sem deixar de ser livre.
  const accountSuggestions = (chosenPartner?.accounts ?? []).flatMap((account) => {
    const label = account.label ?? account.bankName ?? account.number;
    const keys = account.pixKeys.map((key) => key.label ?? key.value);
    return [label, ...(label ? keys.map((key) => `${label} · ${key}`) : keys)].filter(
      (item): item is string => Boolean(item),
    );
  });

  const isInstallments = form.paymentTerm === 'INSTALLMENTS';
  const isReimbursing = form.declaredStatus === 'TO_REIMBURSE';
  const installmentCount = Number(form.installmentCount);
  const installmentAmountCents =
    isInstallments && installmentCount > 0 && form.amountCents !== null
      ? Math.floor(form.amountCents / installmentCount)
      : null;

  function change<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  /// PIX, débito e dinheiro sugerem à vista; à vista sugere o vencimento no dia da compra; à prazo
  /// abre o vencimento vazio esperando a data. Tudo alterável depois.
  function changeMethod(paymentMethod: PaymentMethod) {
    const suggestsImmediate = ['PIX', 'DEBIT', 'CASH'].includes(paymentMethod);
    setForm((current) =>
      suggestsImmediate && current.paymentTerm !== 'IMMEDIATE'
        ? { ...current, paymentMethod, paymentTerm: 'IMMEDIATE', dueOn: current.purchasedOn }
        : { ...current, paymentMethod },
    );
  }

  function changeTerm(paymentTerm: PaymentTerm) {
    setForm((current) => ({
      ...current,
      paymentTerm,
      dueOn: paymentTerm === 'IMMEDIATE' ? current.purchasedOn : null,
      installmentCount: paymentTerm === 'INSTALLMENTS' ? current.installmentCount : '',
    }));
  }

  function changePurchasedOn(purchasedOn: CivilDate | null) {
    setForm((current) => ({
      ...current,
      purchasedOn,
      dueOn: current.paymentTerm === 'IMMEDIATE' ? purchasedOn : current.dueOn,
    }));
  }

  /// Compra da empresa só aceita a trilha de pagamento; compra de sócio ou terceiro, a de reembolso.
  function changePayer(paidBy: PaidBy) {
    setForm((current) => {
      const allowed = declarableStatuses(paidBy);
      const declaredStatus = allowed.includes(current.declaredStatus)
        ? current.declaredStatus
        : allowed[0]!;
      return {
        ...current,
        paidBy,
        paidByPartnerId: paidBy === 'PARTNER' ? current.paidByPartnerId : '',
        thirdPartyName: paidBy === 'THIRD_PARTY' ? current.thirdPartyName : '',
        declaredStatus,
        reimburseTo: paidBy === 'THIRD_PARTY' ? '' : current.reimburseTo,
      };
    });
  }

  function changePartner(partnerId: string) {
    const partner = (partners.data ?? []).find((item) => item.id === partnerId);
    setForm((current) => ({
      ...current,
      paidByPartnerId: partnerId,
      // O nome do sócio entra como sugestão editável de para quem reembolsar.
      reimburseTo:
        current.reimburseTo === '' && partner ? partner.displayName : current.reimburseTo,
    }));
  }

  /// Mesma ideia do sócio: quem pagou é quem costuma receber de volta, então o nome digitado
  /// acompanha "Reembolsar para" enquanto o operador não escrever outra coisa lá.
  function changeThirdParty(thirdPartyName: string) {
    setForm((current) => ({
      ...current,
      thirdPartyName,
      reimburseTo:
        current.reimburseTo === current.thirdPartyName ? thirdPartyName : current.reimburseTo,
    }));
  }

  function openCreate() {
    setEditing(undefined);
    setForm(emptyForm(today));
    setPendingReceipts([]);
    setFormError(undefined);
    setFormOpen(true);
  }

  function openEdit(entry: Entry) {
    setEditing(entry);
    setForm(fromEntry(entry));
    setPendingReceipts([]);
    setFormError(undefined);
    setFormOpen(true);
  }

  /// Anexa o que ficou pendente e DEVOLVE a recusa em vez de lançá-la: o lançamento já está salvo
  /// aqui, então isto não é falha de gravação. Lançar faria a mensagem se misturar com a de salvar,
  /// e a pessoa acharia que perdeu a compra.
  async function attachPending(entryId: string): Promise<string | undefined> {
    for (const item of pendingReceipts) {
      try {
        if (item.kind === 'file') await entriesApi.attachReceipt(entryId, item.file);
        else await entriesApi.attachReceiptFromLink(entryId, item.url);
      } catch (error) {
        return `Lançamento salvo, mas o comprovante não foi anexado — ${describePending(item)}: ${getApiError(error)}`;
      }
    }
    return undefined;
  }

  async function save() {
    setFormError(undefined);
    const description = form.description.trim();
    if (description === '') return setFormError('Descreva o que foi comprado.');
    if (form.amountCents === null || form.amountCents <= 0) {
      return setFormError('Informe o valor da compra.');
    }
    if (form.purchasedOn === null) return setFormError('Informe a data da compra.');
    if (form.paidBy === 'PARTNER' && form.paidByPartnerId === '') {
      return setFormError('Escolha qual sócio pagou a compra.');
    }
    if (form.paidBy === 'THIRD_PARTY' && form.thirdPartyName.trim() === '') {
      return setFormError('Informe o nome de quem pagou a compra.');
    }
    if (form.paymentTerm === 'DEFERRED' && form.dueOn === null) {
      return setFormError('Informe o vencimento da compra a prazo.');
    }
    if (isInstallments) {
      if (!Number.isInteger(installmentCount) || installmentCount < 2) {
        return setFormError('Informe a quantidade de parcelas.');
      }
      if (form.dueOn === null) return setFormError('Informe o vencimento da primeira parcela.');
    }
    if (isReimbursing && form.reimburseTo.trim() === '') {
      return setFormError('Informe para quem reembolsar.');
    }
    const payingOutside = !editing && form.paidBy === 'COMPANY' && form.paidOutside;
    if (payingOutside && form.paidOutsideOn === null) {
      return setFormError('Informe a data do pagamento.');
    }

    const payload: EntryInput = {
      purchasedOn: form.purchasedOn,
      description,
      amountCents: form.amountCents,
      paymentMethod: form.paymentMethod,
      paymentTerm: form.paymentTerm,
      paidBy: form.paidBy,
      paidByPartnerId: form.paidBy === 'PARTNER' ? form.paidByPartnerId : null,
      thirdPartyName: form.paidBy === 'THIRD_PARTY' ? form.thirdPartyName.trim() : null,
      dueOn: form.dueOn,
      installmentCount: isInstallments ? installmentCount : null,
      declaredStatus: form.declaredStatus,
      payeeName: form.paidBy === 'COMPANY' ? form.payeeName.trim() || null : null,
      paidOutsideOn: payingOutside ? form.paidOutsideOn : null,
      reimburseTo: isReimbursing ? form.reimburseTo.trim() : null,
      reimburseAt: isReimbursing ? form.reimburseAt.trim() || null : null,
      contributionId: form.contributionId || null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      const saved = editing
        ? await entriesApi.update(editing.id, payload)
        : await entriesApi.create(payload);
      // Os anexos vêm depois de o lançamento existir, e a falha de um deles não desfaz a compra.
      const attachError = await attachPending(saved.id);
      await queryClient.invalidateQueries({ queryKey: ['company', 'entries'] });
      if (attachError) {
        // O diálogo fica aberto com o que sobrou pendente, para a pessoa tentar de novo.
        setFormError(attachError);
        return;
      }
      setPendingReceipts([]);
      setFormOpen(false);
    } catch (error) {
      setFormError(getApiError(error));
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  function toggleSort(column: EntrySort) {
    if (sort === column) onView({ direction: direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'description' ? 'asc' : 'desc' });
  }

  /// A ação da linha não pode disparar também o clique da linha, que abriria a ficha por cima.
  function stop(action: () => void) {
    return (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      action();
    };
  }

  const actionsOf = (entry: Entry) => (
    <div className="flex justify-end gap-1">
      {capabilities.canSettle && isSettleable(entry) ? (
        <IconButton
          label="Parcelas"
          icon={<CheckCheck className="size-4" />}
          onClick={stop(() => setInstallmentsTarget(entry))}
        />
      ) : null}
      <IconButton
        label="Comprovantes"
        icon={<Paperclip className="size-4" />}
        onClick={stop(() => openEntryTab(entry.id, 'receipts'))}
      />
      {/* Só na compra em boleto: o carnê é a razão de a linha digitável existir,
                            e o botão em toda linha viraria ruído. */}
      {entry.paymentMethod === 'BANK_SLIP' ? (
        <IconButton
          label="Boletos"
          icon={<Barcode className="size-4" />}
          onClick={stop(() => openEntryTab(entry.id, 'slips'))}
        />
      ) : null}
      {capabilities.canUpdate ? (
        <IconButton
          label="Editar"
          icon={<PencilLine className="size-4" />}
          onClick={stop(() => openEdit(entry))}
        />
      ) : null}
      {capabilities.canDelete ? (
        <IconButton
          label="Excluir"
          tone="danger"
          icon={<Trash2 className="size-4" />}
          onClick={stop(() => setDeleteTarget(entry))}
        />
      ) : null}
    </div>
  );

  const totals = page.data?.totals;
  const periodSettlements = page.data?.periodSettlements ?? null;
  const entries = page.data?.entries ?? [];
  const total = page.data?.total ?? 0;

  /// A ficha guarda o id, não a cópia do lançamento: depois de dar baixa numa parcela ou de editar,
  /// a lista se atualiza e a ficha tem de mostrar o novo estado, não o de quando abriu.
  ///
  /// O id vem da URL, e por isso a ficha é buscada pelo id em vez de procurada na página carregada:
  /// quem abre o link não veio da lista, e o recorte dele pode nem incluir aquela linha. Quando a
  /// linha está na página, ela serve de valor inicial: a ficha abre sem piscar, e a busca ainda
  /// sai por baixo para o caso de a linha estar velha.
  const open = useQuery({
    queryKey: ['company', 'entry', openEntryId],
    queryFn: () => entriesApi.get(openEntryId),
    enabled: Boolean(openEntryId),
    retry: false,
    ...(entries.find((item) => item.id === openEntryId)
      ? { initialData: entries.find((item) => item.id === openEntryId) }
      : {}),
  });
  const details = openEntryId ? (open.data ?? undefined) : undefined;

  /// Dois diálogos abertos brigam pelo foco, então a ficha sai de cena enquanto a ação está
  /// aberta — e VOLTA quando ela fecha, porque foi da ficha que a pessoa saiu. Fechar o
  /// comprovante e cair na lista faz perder o lugar.
  const actionOpen = Boolean(formOpen || installmentsTarget || deleteTarget);

  return (
    <section aria-labelledby="company-entries-title">
      <PageHeader
        trail="Investimento inicial / Lançamentos"
        id="company-entries-title"
        title="Lançamentos"
        description="Cada compra do investimento: como foi paga, por quem e o que falta reembolsar."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={openCreate}>Registrar lançamento</CreateButton>
            ) : null}
          </>
        }
      />

      {totals ? (
        <SummaryStrip
          label="Resumo dos lançamentos"
          items={[
            { label: 'Comprado', value: formatCurrency(totals.purchasedCents) },
            {
              label: 'Reembolsado',
              value: formatCurrency(totals.reimbursedCents),
              tone: 'positive',
            },
            {
              label: 'Falta',
              value: formatCurrency(totals.outstandingCents),
              tone: 'warning',
            },
          ]}
        />
      ) : null}
      {/* Só com o recorte sobre a baixa: aí a pergunta é quanto saiu naqueles dias, e o valor inteiro
          das compras da lista não responde — a parcela de outubro não saiu em setembro. */}
      {periodSettlements ? (
        <SummaryStrip
          label="Baixas no período"
          items={[
            {
              label: 'Reembolsado no período',
              value: formatCurrency(periodSettlements.reimbursedCents),
              tone: 'positive',
            },
            {
              label: 'Pago no período',
              value: formatCurrency(periodSettlements.paidCents),
              tone: 'positive',
            },
          ]}
        />
      ) : null}

      {consolidated ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary"
        >
          Visão consolidada: lançamentos de todas as suas unidades, só para consulta. Para registrar
          ou alterar, escolha uma unidade no seletor acima.
        </p>
      ) : null}

      <FilterBar
        searchId="entry-search"
        searchLabel="Buscar lançamento"
        searchPlaceholder="Descrição, quem pagou ou conta"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={clearFilters}
        count={`${String(total)} ${total === 1 ? 'lançamento' : 'lançamentos'}`}
        resultsLabel={`Ver ${String(total)} ${total === 1 ? 'lançamento' : 'lançamentos'}`}
        pageSize={limit}
        onPageSizeChange={(size) => onView({ limit: size })}
      >
        <MultiSelect
          label="Situação"
          className="lg:w-52"
          options={statusOptions}
          selected={statuses ?? statusOptions.map((option) => option.value)}
          onChange={(values) => onView({ statuses: values as EntryStatus[] })}
          itemNoun="situações"
          searchPlaceholder="Buscar situação"
        />
        <FilterGroup
          label="Referência dos dias"
          description="O intervalo vale sobre a compra, o vencimento ou a baixa"
          // "Vence em", e não "Vencimento": a coluna de ordenação já se chama assim, e dois botões
          // com o mesmo nome na mesma tela deixam de dizer qual faz o quê. "Baixado em" é o dia em
          // que o reembolso ou o pagamento aconteceu, pela conciliação ou pela mão.
          options={[
            { value: 'purchasedOn', label: 'Comprado em' },
            { value: 'dueOn', label: 'Vence em' },
            { value: 'settledOn', label: 'Baixado em' },
          ]}
          value={periodBy}
          onChange={(value) => onView({ periodBy: value })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label="Período dos lançamentos"
            value={{ from, to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={today}
          />
        </div>
        <MultiSelect
          label="Forma"
          className="lg:w-44"
          options={methodOptions}
          selected={methods ?? methodOptions.map((option) => option.value)}
          onChange={(values) => onView({ methods: values as PaymentMethod[] })}
          itemNoun="formas"
          searchPlaceholder="Buscar forma"
        />
        <MultiSelect
          label="Pago por"
          className="lg:w-52"
          options={payerOptions}
          selected={payers ?? payerOptions.map((option) => option.value)}
          onChange={(values) => onView({ payers: values })}
          itemNoun="pagadores"
          allLabel="Todos"
          noneLabel="Nenhum"
          searchPlaceholder="Buscar sócio"
        />
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando lançamentos" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : entries.length ? (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? (
            <table className="w-full border-collapse text-left">
              <caption className="sr-only">Lançamentos do investimento inicial</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={sort}
                      direction={direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Situação
                  </th>
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {entries.map((entry) => (
                  // A linha inteira abre a ficha: ver o lançamento não pode depender de acertar um
                  // ícone de 36 pixels. Os botões de ação param a propagação para não abrir a ficha
                  // junto com o que foram acionados.
                  <tr
                    key={entry.id}
                    onClick={() => openEntry(entry.id)}
                    className="cursor-pointer transition hover:bg-content/[.04]"
                  >
                    <td className="whitespace-nowrap px-5 py-4 text-xs text-content-muted">
                      {formatDate(`${entry.purchasedOn}T12:00:00Z`, timeZone)}
                    </td>
                    <td className="px-5 py-4">
                      {/* O clique na linha é do ponteiro; este botão é o mesmo caminho pelo
                          teclado, e leva o nome da compra como rótulo. */}
                      <button
                        type="button"
                        onClick={() => openEntry(entry.id)}
                        className="block rounded text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
                      >
                        {entry.description}
                        <span className="sr-only"> — ver lançamento</span>
                      </button>
                      <span className="mt-1 block text-xs text-content-faint">
                        {methodLabels[entry.paymentMethod]} · {termLabels[entry.paymentTerm]} ·{' '}
                        {payerNameOf(entry)}
                      </span>
                      {entry.reimburseTo ? (
                        <span className="mt-1 block text-xs text-content-faint">
                          Reembolsar para {entry.reimburseTo}
                          {entry.reimburseAt ? ` · ${entry.reimburseAt}` : ''}
                        </span>
                      ) : null}
                      {entry.contribution ? (
                        <span className="mt-1 block text-xs text-content-faint">
                          Aporte: {entry.contribution.description}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-sm tabular-nums">
                      {formatCurrency(entry.amountCents)}
                      {entry.installmentCount ? (
                        <span className="mt-1 block text-xs text-content-faint">
                          {entry.installmentCount}x de{' '}
                          {formatCurrency(entry.installmentAmountCents ?? 0)}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-xs text-content-muted">
                      {entry.dueOn ? formatDate(`${entry.dueOn}T12:00:00Z`, timeZone) : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <StatusPill tone={statusTones[entry.status]}>
                        {statusLabels[entry.status]}
                      </StatusPill>
                      {entry.reimbursedCents > 0 && entry.outstandingCents > 0 ? (
                        <span className="mt-1 block text-xs text-content-faint">
                          Falta {formatCurrency(entry.outstandingCents)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-4">
                      {/* Só ícones: com uma dúzia de linhas, botão com texto vira uma parede de
                          retângulos que disputa atenção com o dado. A dica diz o que cada um faz, e
                          o rótulo continua no nome acessível para quem não enxerga. */}
                      {actionsOf(entry)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <>
              <SortBar
                columns={columns}
                sort={sort}
                direction={direction}
                onToggle={toggleSort}
                label="Ordenar os lançamentos"
              />
              {/* Celular: um card por compra, cada um abrindo a ficha. Espremer seis colunas e
                  cinco botões em 390 pixels só produz barra de rolagem lateral — e ninguém rola
                  uma tabela de lado para ler a própria lista. */}
              <ul className="divide-y divide-surface-border">
                {entries.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      onClick={() => openEntry(entry.id)}
                      className="flex w-full items-start gap-3 px-4 py-4 text-left transition hover:bg-content/[.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/60"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{entry.description}</span>
                        <span className="mt-1 block text-xs text-content-faint">
                          {formatDate(`${entry.purchasedOn}T12:00:00Z`, timeZone)} ·{' '}
                          {methodLabels[entry.paymentMethod]} · {payerNameOf(entry)}
                        </span>
                        <span className="mt-2 flex flex-wrap items-center gap-2">
                          <StatusPill tone={statusTones[entry.status]}>
                            {statusLabels[entry.status]}
                          </StatusPill>
                          {entry.reimbursedCents > 0 && entry.outstandingCents > 0 ? (
                            <span className="text-xs text-content-faint">
                              Falta {formatCurrency(entry.outstandingCents)}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block text-sm font-semibold tabular-nums">
                          {formatCurrency(entry.amountCents)}
                        </span>
                        {entry.installmentCount ? (
                          <span className="mt-1 block text-xs text-content-faint">
                            {entry.installmentCount}x de{' '}
                            {formatCurrency(entry.installmentAmountCents ?? 0)}
                          </span>
                        ) : null}
                        {entry.dueOn ? (
                          <span className="mt-1 block text-xs text-content-faint">
                            vence {formatDate(`${entry.dueOn}T12:00:00Z`, timeZone)}
                          </span>
                        ) : null}
                      </span>
                    </button>
                    <div className="px-4 pb-3">{actionsOf(entry)}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onOffsetChange={(next) => onView({ offset: next })}
            itemLabel={(count) => (count === 1 ? 'lançamento' : 'lançamentos')}
          />
        </div>
      ) : (
        <EmptyCard
          icon={Receipt}
          title="Nenhum lançamento encontrado"
          description="Ajuste os filtros ou registre a primeira compra do investimento."
        />
      )}

      <Modal
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editing ? 'Editar lançamento' : 'Registrar lançamento'}
        description="A condição do pagamento define o vencimento; quem pagou define a situação."
      >
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="grid gap-5">
            <Input
              label="Descrição"
              autoComplete="off"
              value={form.description}
              onChange={(event) => change('description', event.target.value)}
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <DatePicker
                label="Data da compra"
                value={form.purchasedOn}
                onValueChange={changePurchasedOn}
                today={today}
              />
              <CurrencyInput
                label="Valor"
                hint="Valor total do que foi comprado."
                value={form.amountCents}
                onValueChange={(cents) => change('amountCents', cents)}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <Select
                label="Forma de pagamento"
                value={form.paymentMethod}
                options={(Object.keys(methodLabels) as PaymentMethod[]).map((value) => ({
                  value,
                  label: methodLabels[value],
                }))}
                onValueChange={(value) => changeMethod(value as PaymentMethod)}
              />
              <Select
                label="Condição"
                value={form.paymentTerm}
                options={(Object.keys(termLabels) as PaymentTerm[]).map((value) => ({
                  value,
                  label: termLabels[value],
                }))}
                onValueChange={(value) => changeTerm(value as PaymentTerm)}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <DatePicker
                label={isInstallments ? 'Vencimento da primeira parcela' : 'Vencimento'}
                value={form.dueOn}
                onValueChange={(value) => change('dueOn', value)}
                today={today}
                {...(form.paymentTerm === 'DEFERRED'
                  ? { hint: 'Obrigatório na compra a prazo.' }
                  : {})}
              />
              {isInstallments ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Quantidade de parcelas"
                    inputMode="numeric"
                    autoComplete="off"
                    value={form.installmentCount}
                    onChange={(event) =>
                      change('installmentCount', event.target.value.replace(/\D/gu, ''))
                    }
                  />
                  <Input
                    label="Valor da parcela"
                    readOnly
                    hint="Calculado."
                    value={
                      installmentAmountCents === null ? '' : formatCurrency(installmentAmountCents)
                    }
                  />
                </div>
              ) : null}
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <Select
                label="Pago por"
                value={form.paidBy}
                options={(Object.keys(payerLabels) as PaidBy[]).map((value) => ({
                  value,
                  label: payerLabels[value],
                }))}
                onValueChange={(value) => changePayer(value as PaidBy)}
              />
              {form.paidBy === 'PARTNER' ? (
                <Select
                  label="Sócio que pagou"
                  value={form.paidByPartnerId}
                  options={partnerOptions}
                  onValueChange={changePartner}
                />
              ) : null}
              {form.paidBy === 'THIRD_PARTY' ? (
                <Input
                  label="Nome de quem pagou"
                  autoComplete="off"
                  hint="Quem pagou sem ser sócio nem a empresa."
                  value={form.thirdPartyName}
                  onChange={(event) => changeThirdParty(event.target.value)}
                />
              ) : null}
            </div>

            {/* A compra da empresa não declara situação: ela sai da baixa das parcelas. O que a
                pessoa informa é por onde o dinheiro saiu — e é isso que decide se a parcela espera
                a conciliação do extrato ou já nasce baixada (ADR 0025). */}
            {form.paidBy === 'COMPANY' ? (
              <div className="grid gap-5 sm:grid-cols-2">
                <Input
                  label="Favorecido"
                  autoComplete="off"
                  hint="Quem recebeu o dinheiro. Opcional, mas ajuda a conciliar."
                  value={form.payeeName}
                  onChange={(event) => change('payeeName', event.target.value)}
                />
                {editing ? null : (
                  <Select
                    label="Como foi pago"
                    value={form.paidOutside ? 'OUTSIDE' : 'ACCOUNT'}
                    options={[
                      { value: 'ACCOUNT', label: 'Pela conta da empresa' },
                      { value: 'OUTSIDE', label: 'Por fora da conta, já pago' },
                    ]}
                    onValueChange={(value) => change('paidOutside', value === 'OUTSIDE')}
                  />
                )}
                {!editing && form.paidOutside ? (
                  <DatePicker
                    label="Pago em"
                    value={form.paidOutsideOn}
                    onValueChange={(value) => change('paidOutsideOn', value)}
                    today={today}
                  />
                ) : null}
              </div>
            ) : (
              <Select
                label="Situação"
                value={form.declaredStatus}
                options={declarableStatuses(form.paidBy).map((value) => ({
                  value,
                  label: statusLabels[value],
                }))}
                onValueChange={(value) => change('declaredStatus', value as EntryStatus)}
              />
            )}

            {isReimbursing ? (
              <div className="grid gap-5 sm:grid-cols-2">
                <Input
                  label="Reembolsar para"
                  autoComplete="off"
                  value={form.reimburseTo}
                  onChange={(event) => change('reimburseTo', event.target.value)}
                />
                <div className="space-y-2">
                  <Input
                    label="Reembolsar onde"
                    autoComplete="off"
                    hint="Conta, cartão ou chave. Opcional."
                    value={form.reimburseAt}
                    onChange={(event) => change('reimburseAt', event.target.value)}
                  />
                  {accountSuggestions.length ? (
                    <Combobox
                      label="Contas cadastradas do sócio"
                      placeholder="Buscar conta cadastrada"
                      selectedLabel={
                        accountSuggestions.includes(form.reimburseAt) ? form.reimburseAt : ''
                      }
                      options={accountSuggestions.map((suggestion) => ({
                        value: suggestion,
                        label: suggestion,
                      }))}
                      clearable={false}
                      onSelect={(choice) => {
                        if (choice.kind === 'option') change('reimburseAt', choice.value);
                      }}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            <Select
              label="Aporte de origem"
              value={form.contributionId}
              options={contributionOptions}
              onValueChange={(value) => change('contributionId', value)}
            />

            <Textarea
              label="Observação"
              rows={2}
              value={form.notes}
              onChange={(event) => change('notes', event.target.value)}
            />

            {capabilities.canManageReceipts ? (
              <ReceiptPicker
                label={editing ? 'Anexar mais comprovantes' : 'Comprovantes'}
                pending={pendingReceipts}
                onAdd={(items) => setPendingReceipts((current) => [...current, ...items])}
                onRemove={(index) =>
                  setPendingReceipts((current) => current.filter((_item, at) => at !== index))
                }
              />
            ) : null}
          </div>

          {formError ? <RequestAlert message={formError} /> : null}

          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              onClick={() => setFormOpen(false)}
            >
              Cancelar
            </Button>
            <Button className="w-full sm:w-auto" loading={saving}>
              {editing ? 'Salvar lançamento' : 'Registrar lançamento'}
            </Button>
          </div>
        </form>
      </Modal>

      {details && !actionOpen && !entryTab ? (
        <EntryDetailsDialog
          entry={details}
          timeZone={timeZone}
          onClose={closeEntry}
          {...(capabilities.canSettle && isSettleable(details)
            ? { onSettle: () => setInstallmentsTarget(details) }
            : {})}
          onReceipts={() => onView({ entryTab: 'receipts' })}
          {...(details.paymentMethod === 'BANK_SLIP'
            ? { onSlips: () => onView({ entryTab: 'slips' }) }
            : {})}
          {...(capabilities.canUpdate ? { onEdit: () => openEdit(details) } : {})}
          {...(capabilities.canDelete ? { onDelete: () => setDeleteTarget(details) } : {})}
        />
      ) : null}

      {installmentsTarget ? (
        <InstallmentsDialog
          initial={installmentsTarget}
          today={today}
          timeZone={timeZone}
          onClose={() => setInstallmentsTarget(undefined)}
          onChanged={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'entries'] });
          }}
        />
      ) : null}

      {/* Fechar comprovantes ou boletos volta para a ficha, e não para a lista: foi da ficha que a
          pessoa saiu. Antes isso era uma regra escrita à mão; agora é o próprio endereço — tirar a
          aba deixa a ficha. */}
      {details && entryTab === 'slips' ? (
        <EntrySlipsDialog
          entry={details}
          today={today}
          timeZone={timeZone}
          canManage={capabilities.canManageSlips}
          onClose={() => onView({ entryTab: '' })}
        />
      ) : null}

      {details && entryTab === 'receipts' ? (
        <EntryReceiptsDialog
          entry={details}
          timeZone={timeZone}
          canManage={capabilities.canManageReceipts}
          onClose={() => onView({ entryTab: '' })}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteDialog
          entry={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
          onDeleted={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'entries'] });
            setDeleteTarget(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

/// Dar baixa parcela a parcela é o que move a situação. O diálogo guarda a própria cópia e usa a
/// resposta do servidor, para a situação mudar na hora.
function InstallmentsDialog({
  initial,
  today,
  timeZone,
  onClose,
  onChanged,
}: {
  initial: Entry;
  today: CivilDate;
  timeZone: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [entry, setEntry] = useState(initial);
  const [settledOn, setSettledOn] = useState<CivilDate | null>(today);
  /// A mesma lista serve às duas trilhas; o que muda é o nome do dinheiro que saiu (ADR 0025).
  const paying = entry.declaredStatus === 'PAYMENT_PENDING';
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function settle(installmentNumber: number) {
    setError(undefined);
    if (settledOn === null) {
      return setError(paying ? 'Informe a data do pagamento.' : 'Informe a data do reembolso.');
    }
    setBusy(true);
    try {
      setEntry(await entriesApi.settle(entry.id, { installmentNumber, settledOn, notes: null }));
      await onChanged();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
    return undefined;
  }

  async function clear(installmentNumber: number) {
    setError(undefined);
    setBusy(true);
    try {
      setEntry(await entriesApi.clear(entry.id, installmentNumber));
      await onChanged();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title={paying ? 'Parcelas pagas' : 'Parcelas reembolsadas'}
      description={`${entry.description} · ${formatCurrency(entry.amountCents)}`}
    >
      <div className="mb-5 flex flex-wrap gap-4 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs">
        <span>
          {settledLabelOf(entry)}:{' '}
          <strong className="tabular-nums text-positive">
            {formatCurrency(entry.reimbursedCents)}
          </strong>
        </span>
        <span>
          Falta:{' '}
          <strong className="tabular-nums text-warning">
            {formatCurrency(entry.outstandingCents)}
          </strong>
        </span>
        <span>
          Situação: <strong>{statusLabels[entry.status]}</strong>
        </span>
      </div>

      <div className="mb-5">
        <DatePicker
          label={paying ? 'Data do pagamento' : 'Data do reembolso'}
          value={settledOn}
          onValueChange={setSettledOn}
          today={today}
          hint={
            paying
              ? 'Usada ao baixar por fora da conta. O que saiu pela conta é conciliado no Financeiro.'
              : 'Usada ao dar baixa nas parcelas abaixo.'
          }
        />
      </div>

      <ul className="divide-y divide-surface-border rounded-xl border border-surface-border">
        {entry.installments.map((installment) => (
          <li key={installment.number} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="text-sm font-medium">
              {installment.number}/{entry.installments.length}
            </span>
            <span className="text-sm tabular-nums">{formatCurrency(installment.amountCents)}</span>
            {installment.dueOn ? (
              <span className="text-xs text-content-muted">
                vence {formatDate(`${installment.dueOn}T12:00:00Z`, timeZone)}
              </span>
            ) : null}
            {installment.settledOn ? (
              <span className="text-xs text-positive">
                {paying ? 'paga' : 'reembolsada'} em{' '}
                {formatDate(`${installment.settledOn}T12:00:00Z`, timeZone)}
              </span>
            ) : installment.settledCents > 0 ? (
              // A conciliação pode cobrir só parte da parcela; sem isto ela pareceria intocada.
              <span className="text-xs text-warning">
                {paying ? 'paga' : 'reembolsada'} em parte:{' '}
                <span className="tabular-nums">{formatCurrency(installment.settledCents)}</span> ·
                falta{' '}
                <span className="tabular-nums">
                  {formatCurrency(installment.amountCents - installment.settledCents)}
                </span>
              </span>
            ) : null}
            <span className="ml-auto">
              {installment.settledOn ? (
                <IconButton
                  label="Desfazer"
                  icon={<Undo2 />}
                  disabled={busy}
                  onClick={() => void clear(installment.number)}
                />
              ) : (
                <IconButton
                  label="Dar baixa"
                  icon={<CheckCheck />}
                  disabled={busy}
                  onClick={() => void settle(installment.number)}
                />
              )}
            </span>
          </li>
        ))}
      </ul>

      {error ? <RequestAlert message={error} /> : null}

      <div className="mt-7 flex justify-end border-t border-surface-border pt-5">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Concluir
        </Button>
      </div>
    </Modal>
  );
}

function DeleteDialog({
  entry,
  onClose,
  onDeleted,
}: {
  entry: Entry;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const settled = entry.installments.filter((installment) => installment.settledOn !== null).length;

  async function confirm() {
    setError(undefined);
    setBusy(true);
    try {
      await entriesApi.remove(entry.id);
      await onDeleted();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Excluir lançamento"
      description="A compra sai da conciliação junto com as baixas registradas nela."
    >
      <p className="text-sm leading-6 text-content-secondary">
        Excluir <strong>{entry.description}</strong>, de {formatCurrency(entry.amountCents)}?
        {settled > 0 ? ` ${settled} parcela(s) já baixada(s) também serão apagadas.` : ''}
      </p>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={busy}
          onClick={() => void confirm()}
        >
          Excluir lançamento
        </Button>
      </div>
    </Modal>
  );
}
